import { useEffect, useState } from "react";

import { useCart, useRoofingEstimator } from "../../../hooks/query";
import type { LaborRowItem } from "../types";
import {
    buildLaborEditsFromRows,
    mapLaborLineItemsResponse,
} from "../utils/laborLineItems";
import { hydrateLaborFromCart } from "../utils/cartMapping";

import { useEstimateContractContextManager } from "./";

/** Replace labor cart ids for this option without wiping material cart ids. */
function replaceLaborCartIds(
    previous: Record<string, string>,
    laborEditKeys: string[],
    nextLaborCartIds: Record<string, string>,
): Record<string, string> {
    const merged = { ...previous };
    for (const key of laborEditKeys) {
        delete merged[key];
    }
    return { ...merged, ...nextLaborCartIds };
}

export default function useLaborInit(
    projectId?: string,
    estimateOptionId?: string | null,
    optionCartRevision = 0,
) {
    const { setValues, setReferenceValue } = useEstimateContractContextManager();
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [laborRows, setLaborRows] = useState<LaborRowItem[]>([]);

    const { useLaborLineItems } = useRoofingEstimator();
    const laborLineItemsSvc = useLaborLineItems();
    const { useListByProjectId } = useCart();
    const listCartSvc = useListByProjectId();

    const loadLaborData = async (
        id: string,
        optionId?: string | null,
        isCancelled?: () => boolean,
    ) => {
        setLoading(true);
        setError(null);
        try {
            const data = await laborLineItemsSvc.mutateAsync({
                ProjectId: id,
            });
            if (isCancelled?.()) return { success: false };

            const rows = mapLaborLineItemsResponse(data);
            const baseEdits = buildLaborEditsFromRows(rows);
            let labor = baseEdits;
            let cartIds: Record<string, string> = {};

            if (Object.keys(baseEdits).length > 0) {
                const cartItems = await listCartSvc.mutateAsync({
                    ProjectId: id,
                    ...(optionId ? { EstimateOptionId: optionId } : {}),
                });
                if (isCancelled?.()) return { success: false };

                const hydrated = hydrateLaborFromCart(
                    Array.isArray(cartItems) ? cartItems : [],
                    rows,
                    baseEdits,
                );
                labor = hydrated.labor;
                cartIds = hydrated.cartIds;
            }

            if (isCancelled?.()) return { success: false };

            setLaborRows(rows);
            const applyLoadedLabor = (prev: any) => ({
                ...prev,
                labor,
                cartIds: replaceLaborCartIds(
                    prev.cartIds || {},
                    Object.keys(baseEdits),
                    cartIds,
                ),
                estimateOptionId: optionId ?? prev.estimateOptionId ?? null,
            });

            setValues(applyLoadedLabor);
            setReferenceValue(applyLoadedLabor);

            return { success: true };
        } catch (err: any) {
            if (!isCancelled?.()) {
                setError(err.message || "Failed to load labor");
            }
            return { success: false, error: err };
        } finally {
            if (!isCancelled?.()) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        if (!projectId) return;

        let cancelled = false;
        loadLaborData(projectId, estimateOptionId, () => cancelled);

        return () => {
            cancelled = true;
        };
    }, [projectId, estimateOptionId, optionCartRevision]);

    return {
        loadLaborData,
        laborRows,
        loading,
        error,
    };
}
